import type { CatalogMetaResponse } from "./catalog.type";

export type CatalogRouteKind = "category" | "brand" | "search" | "other";

export interface CatalogRouteInfo {
  kind: CatalogRouteKind;
  /** category || brand → categoria navega por path (drill), não por query param */
  isCategoryDrill: boolean;
  /** Prefixo travado da rota: "/c" ou "/b/<marca>". "" quando não há drill. */
  basePrefix: string;
  /** Path de categoria após a base. "" quando não há categoria selecionada. */
  categoryPath: string;
}

/**
 * Classifica a rota de catálogo e separa a base travada do path de categoria.
 *
 * Uma rota `/b/<marca>` é uma rota de categorias com a marca travada no path:
 * a base é `/b/<marca>` (2 segmentos) e a categoria é tudo após isso. Por isso
 * `/b/nike/roupas` tem categoria no nível 1, equivalente a `/c/roupas`.
 */
export function parseCatalogRoute(pathname: string): CatalogRouteInfo {
  const segments = pathname.split("/").filter(Boolean);
  const prefix = segments[0];

  if (prefix === "c") {
    const basePrefix = `/${prefix}`;
    return {
      kind: "category",
      isCategoryDrill: true,
      basePrefix,
      categoryPath: pathname.slice(basePrefix.length),
    };
  }

  if (prefix === "b" && segments.length >= 2) {
    const basePrefix = `/${segments[0]}/${segments[1]}`;
    return {
      kind: "brand",
      isCategoryDrill: true,
      basePrefix,
      categoryPath: pathname.slice(basePrefix.length),
    };
  }

  return {
    kind: prefix === "s" ? "search" : "other",
    isCategoryDrill: false,
    basePrefix: "",
    categoryPath: "",
  };
}

/** Last meaningful path segment — used as the slug fallback for labels/SEO. */
export function getRouteSlug(pathname: string): string {
  const segments = pathname.split("/").filter(Boolean);
  return segments[segments.length - 1] ?? "";
}

/**
 * The slug carried in the URL is sanitized (lowercase, no accents/special
 * characters), so deriving a label from it strips characters like "ç"/"ã"
 * ("calcados-femininos" → "Calcados Femininos"). The API breadcrumbs expose the
 * human-readable name with the original characters preserved, so callers prefer
 * the last meaningful breadcrumb when available.
 */
export function getCurrentBreadcrumbName(
  meta?: CatalogMetaResponse,
): string | undefined {
  const crumbs = meta?.breadcrumbs?.filter((crumb) => crumb.key !== "home");
  return crumbs?.[crumbs.length - 1]?.name?.trim() || undefined;
}
